import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

declare const __APP_BUILD_ID__: string | undefined

const CHECK_MS = 60_000
const TOAST_ID = 'pmms-app-update'

type VersionPayload = { buildId?: string }

function currentBuildId(): string {
  try {
    return typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'dev'
  } catch {
    return 'dev'
  }
}

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const data = (await res.json()) as VersionPayload
    return typeof data.buildId === 'string' && data.buildId ? data.buildId : null
  } catch {
    return null
  }
}

function reloadApp() {
  window.location.reload()
}

function pokeServiceWorkers() {
  if (!('serviceWorker' in navigator)) return
  void navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) void reg.update().catch(() => undefined)
  })
}

/**
 * Detects a new **web** frontend deploy (`/version.json` buildId).
 * Not used for Android/TWA APK wrapper updates — see useTwaWrapperUpdate.
 */
export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const updateReadyRef = useRef(false)
  const localBuildId = currentBuildId()

  const reload = useCallback(() => {
    reloadApp()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (localBuildId === 'dev') return

    let cancelled = false

    const markReady = () => {
      if (cancelled || updateReadyRef.current) return
      updateReadyRef.current = true
      setUpdateAvailable(true)
      toast('Update available', {
        id: TOAST_ID,
        description: 'Reload to apply',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: reloadApp,
        },
      })
    }

    const check = async () => {
      if (cancelled) return
      pokeServiceWorkers()
      const remote = await fetchRemoteBuildId()
      if (cancelled || !remote || remote === localBuildId) return
      markReady()
    }

    void check()
    const timer = window.setInterval(() => void check(), CHECK_MS)

    // Every foreground (app switch, screen unlock, tab refocus) used to force
    // an unprompted window.location.reload() once an update looked ready —
    // if /version.json ever answered inconsistently across requests (stale
    // CDN/nginx cache edge, rolling deploy), the reload itself re-triggered
    // this same check and could re-arm updateReadyRef, reloading again on
    // the very next foreground: an invisible refresh loop with no user
    // action able to stop it short of force-closing the app. The toast and
    // AppUpdateBanner already give the user an explicit Reload control —
    // this only needs to keep that state fresh, never reload on its own.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (updateReadyRef.current) return
      void check()
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [localBuildId])

  return { updateAvailable, reload }
}
